/**
 * PDF dingbats (□, •, Wingdings) are list markers. Turn each one into a
 * markdown bullet so the appendix can render a real list.
 */

const BULLET_GLYPH =
    "[\\u2022\\u2023\\u2043\\u2219\\u25E6\\u25AA\\u25AB\\u25CF\\u25CB\\u25A0\\u25A1\\u25FE\\u25FC\\u25FA\\u2610\\u2611\\u2612\\u00B7\\u22C5\\uF0A7\\uF0B7\\u25BA\\u25B8\\u25B6□■▪▫●○◦◆◇►▸‣⁃∙‧◘◙]";

const BULLET_CHAR = new RegExp(BULLET_GLYPH);
const JUNK_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\uE000-\uF8FF\uFFFD]/g;
const INLINE_BULLET_SPLIT = new RegExp(`\\s*${BULLET_GLYPH}\\s*`);
const PACKED_DASH_SPLIT = /\s+-(?=[A-Z][a-z])/;

function looksLikeMarkdownStructure(line) {
    const text = line.trim();
    if (!text) return false;
    if (/^\|/.test(text) || /\|\s*$/.test(text)) return true;
    if (/^#{1,6}\s/.test(text)) return true;
    if (/^```/.test(text)) return true;
    if (/^>\s?/.test(text)) return true;
    return false;
}

function asMarkdownBullet(part) {
    const body = String(part || "")
        .replace(/^[-*+]\s*/, "")
        .replace(/^-/, "")
        .replace(/\s+/g, " ")
        .trim();
    return body ? `- ${body}` : "";
}

function expandPackedDashLine(line) {
    const text = String(line || "").trim();
    if (!PACKED_DASH_SPLIT.test(text)) return [line];
    const parts = text
        .split(PACKED_DASH_SPLIT)
        .map((part) => part.replace(/\s+/g, " ").trim())
        .filter(Boolean);
    if (parts.length <= 1) return [line];
    const [first, ...rest] = parts;
    const out = [];
    if (/^[-*+]\s*/.test(text)) {
        const bullet = asMarkdownBullet(first);
        if (bullet) out.push(bullet);
    } else if (first) {
        out.push(first);
    }
    for (const part of rest) {
        const bullet = asMarkdownBullet(part);
        if (bullet) out.push(bullet);
    }
    return out.length ? out : [line];
}

function isLeadingTitle(text) {
    const title = String(text || "").trim();
    if (!title || title.length > 40) return false;
    if (/[.!?]$/.test(title)) return false;
    return /^contingenc(?:y|ies)$/i.test(title) || /^[A-Z][A-Za-z /&-]{2,40}$/.test(title);
}

/**
 * Split one line that packs several □ / • markers into markdown bullets.
 * A short title before the first marker, such as "Contingencies", stays a heading line.
 * @param {string} line
 * @returns {string[]}
 */
export function expandInlineBulletLine(line) {
    const text = String(line || "")
        .replace(/\u00A0/g, " ")
        .replace(JUNK_CHARS, (char) => (BULLET_CHAR.test(char) ? char : ""))
        .trimEnd();
    if (!text.trim()) return [""];
    if (looksLikeMarkdownStructure(text)) return [text];

    const leading = text.match(new RegExp(`^[ \\t]*(?:[-*+]\\s+|${BULLET_GLYPH}\\s*)([\\s\\S]+)$`));
    const startsWithGlyph = new RegExp(`^\\s*${BULLET_GLYPH}`).test(text);
    const body = (leading ? leading[1] : text).trim();
    if (!body) return [""];
    if (!INLINE_BULLET_SPLIT.test(body)) {
        if (startsWithGlyph) {
            const bullet = asMarkdownBullet(body);
            return bullet ? [bullet] : [""];
        }
        return expandPackedDashLine(text);
    }

    const parts = body
        .split(INLINE_BULLET_SPLIT)
        .map((part) => part.replace(/\s+/g, " ").trim())
        .filter(Boolean);
    if (parts.length <= 1) return expandPackedDashLine(text);

    const [first, ...rest] = parts;
    const out = [];
    if (!leading && isLeadingTitle(first)) out.push(first);
    else {
        const bullet = asMarkdownBullet(first);
        if (bullet) out.push(bullet);
    }
    for (const part of rest) {
        const bullet = asMarkdownBullet(part);
        if (bullet) out.push(bullet);
    }
    return out.length ? out : [text];
}

function isMarkdownBullet(line) {
    return /^[-*+]\s+\S/.test(String(line || "").trim());
}

function isTableLine(line) {
    const text = String(line || "").trim();
    if (!text.includes("|")) return false;
    return text.startsWith("|") || (text.match(/\|/g) || []).length >= 2;
}

function appendContinuation(base, next) {
    const left = String(base || "").trimEnd();
    const right = String(next || "").trim();
    if (/[A-Za-z]-$/.test(left) && /^[a-z]/.test(right)) return `${left.slice(0, -1)}${right}`;
    return `${left} ${right}`.replace(/\s+/g, " ").trim();
}

/** PDF text wraps a bullet across lines. Keep those lines on the same item until a blank line. */
function joinWrappedLines(lines) {
    const out = [];
    for (const line of lines) {
        if (!line) {
            if (out.length && out[out.length - 1] !== "") out.push("");
            continue;
        }
        const prev = out.length ? out[out.length - 1] : "";
        if (prev && isMarkdownBullet(prev) && !isMarkdownBullet(line) && !/^#{1,6}\s/.test(line) && !isTableLine(line)) {
            out[out.length - 1] = appendContinuation(prev, line);
            continue;
        }
        out.push(line);
    }
    return out;
}

export function normalizeMarkdownText(value) {
    const raw = String(value ?? "")
        .replace(/\r\n?/g, "\n")
        .replace(/[\u2018\u2019\u201A]/g, "'")
        .replace(/[\u201C\u201D\u201E]/g, '"')
        .replace(/[\u2013\u2014]/g, "-")
        .replace(/\u2026/g, "...");
    const lines = joinWrappedLines(raw.split("\n").flatMap((line) => expandInlineBulletLine(line)));
    return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}
